import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './Card';
import apiService from '../services/api';
import { publicApiService } from '../services/publicApi';
import LoadingSpinner from './LoadingSpinner';
import { TrendingUp, TrendingDown, Minus, RefreshCw, X } from 'lucide-react';
import { formatRelativeTime, formatDate } from '../utils/dateUtils';

interface DirectionalThesis {
  _id: string;
  timeframe: string;
  direction: 'up' | 'sideways' | 'down';
  confidence: number;
  scenario: string;
  targetPrice?: {
    nasdaq?: { current: number; target: number };
    dow?: { current: number; target: number };
    nyse?: { current: number; target: number };
  };
  targetDate: string;
  keyFactors: string[];
  confirmationSignals: string[];
  failingSignals: string[];
  sectorRankings: Array<{
    sector: string;
    correlation: number;
    reasoning: string;
  }>;
  suggestedStrategy: {
    tier1: string;
    tier2: string;
    tier3: string;
    tier4: string;
    tier5: string;
    tier6: string;
  };
  previousContext?: {
    confidence: number;
    generatedAt: string;
    performance?: {
      directionMatch: boolean;
      priceAccuracy: {
        nasdaq?: number;
        dow?: number;
        nyse?: number;
      };
      averageDeviation: number;
    };
  };
  otherDirectionsContext?: {
    up?: {
      confidence: number;
      generatedAt: string;
    };
    sideways?: {
      confidence: number;
      generatedAt: string;
    };
    down?: {
      confidence: number;
      generatedAt: string;
    };
  };
  generatedAt: string;
  marketContext: {
    currentPrices: {
      nasdaq: number;
      dow: number;
      nyse: number;
    };
    overallSentiment: string;
    dataDateReference: string;
  };
  marketDataProvenance?: {
    barIntervalLabel: string;
    priceSource: 'last_bar_close' | 'live_quote';
    freshnessLagMs: number;
    spotFreshnessLagMs?: number;
    spotAt?: string;
    lastBarAt: string;
    barSeries?: {
      daily?: { barIntervalLabel: string; pointCount: number };
      intraday5m?: { barIntervalLabel: string; pointCount: number };
    };
  };
}

const TIMEFRAMES: Array<{
  value: '1_day' | '1_2_weeks' | '1_month' | '6_months_plus';
  label: string;
}> = [
  { value: '1_day', label: '1 Day' },
  { value: '1_2_weeks', label: '1-2 Weeks' },
  { value: '1_month', label: '1 Month' },
  { value: '6_months_plus', label: '6 Months+' },
];

interface MarketThesisProps {
  usePublicApi?: boolean;
}

const MarketThesis: React.FC<MarketThesisProps> = ({ usePublicApi = false }) => {
  const [theses, setTheses] = useState<Record<string, DirectionalThesis[]>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [selectedThesis, setSelectedThesis] =
    useState<DirectionalThesis | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [history, setHistory] = useState<
    Record<string, Record<string, DirectionalThesis[]>>
  >({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadAllTheses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllTheses = async () => {
    // Prevent multiple simultaneous loads
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    const loadingState: Record<string, boolean> = {};
    for (const timeframe of TIMEFRAMES) {
      loadingState[timeframe.value] = true;
    }
    setLoading(loadingState);

    try {
      const promises = TIMEFRAMES.map(async (tf) => {
        try {
          // Use public API if specified (for read-only dashboard)
          const data = usePublicApi
            ? await publicApiService.getLatestDirectionalTheses(tf.value)
            : await apiService.getLatestDirectionalTheses(tf.value);
          return { timeframe: tf.value, data };
        } catch (error) {
          console.error(`Error loading theses for ${tf.value}:`, error);
          return { timeframe: tf.value, data: [] };
        }
      });

      const results = await Promise.all(promises);
      const thesesMap: Record<string, DirectionalThesis[]> = {};
      results.forEach(({ timeframe, data }) => {
        thesesMap[timeframe] = data;
        loadingState[timeframe] = false;
      });

      setTheses(thesesMap);
      setLoading(loadingState);
    } catch (error) {
      console.error('Error loading theses:', error);
      const loadingState: Record<string, boolean> = {};
      for (const timeframe of TIMEFRAMES) {
        loadingState[timeframe.value] = false;
      }
      setLoading(loadingState);
    } finally {
      setIsLoading(false);
    }
  };

  const loadHistory = async (
    timeframe: string,
    direction: 'up' | 'sideways' | 'down',
  ) => {
    if (history[timeframe]?.[direction]) {
      return; // Already loaded
    }

    try {
      const data = await apiService.getDirectionalThesisHistory(
        timeframe as any,
        direction,
      );
      setHistory((prev) => ({
        ...prev,
        [timeframe]: {
          ...(prev[timeframe] || {}),
          [direction]: data,
        },
      }));
    } catch (error) {
      console.error(
        `Error loading history for ${timeframe}-${direction}:`,
        error,
      );
    }
  };

  const handleDirectionClick = async (thesis: DirectionalThesis) => {
    setSelectedThesis(thesis);
    setIsModalOpen(true);
    // Load history when opening modal
    await loadHistory(thesis.timeframe, thesis.direction);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedThesis(null);
  };

  const getDirectionIcon = (direction: 'up' | 'sideways' | 'down') => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="h-5 w-5 text-green-600" />;
      case 'down':
        return <TrendingDown className="h-5 w-5 text-red-600" />;
      case 'sideways':
        return <Minus className="h-5 w-5 text-gray-600" />;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 70) return 'text-green-700 bg-green-50';
    if (confidence >= 50) return 'text-yellow-700 bg-yellow-50';
    if (confidence >= 30) return 'text-orange-700 bg-orange-50';
    return 'text-red-700 bg-red-50';
  };

  const getConfidenceBarColor = (confidence: number) => {
    if (confidence >= 70) return 'bg-green-500';
    if (confidence >= 50) return 'bg-yellow-500';
    if (confidence >= 30) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getDirectionCardBgColor = (
    direction: 'up' | 'sideways' | 'down',
    confidence: number,
    isHighest: boolean,
  ) => {
    if (isHighest) {
      // Highlight the highest confidence direction
      if (direction === 'up') return 'bg-green-50 border-green-300 border-2';
      if (direction === 'down') return 'bg-red-50 border-red-300 border-2';
      return 'bg-yellow-50 border-yellow-300 border-2';
    }

    // Base colors based on direction and confidence
    if (direction === 'up') {
      if (confidence >= 70) return 'bg-green-50/50 border-green-200';
      if (confidence >= 50) return 'bg-green-50/30 border-green-100';
      return 'bg-green-50/20 border-gray-200';
    }
    if (direction === 'down') {
      if (confidence >= 70) return 'bg-red-50/50 border-red-200';
      if (confidence >= 50) return 'bg-red-50/30 border-red-100';
      return 'bg-red-50/20 border-gray-200';
    }
    // sideways
    if (confidence >= 70) return 'bg-gray-50 border-gray-300';
    if (confidence >= 50) return 'bg-gray-50/70 border-gray-200';
    return 'bg-gray-50/50 border-gray-200';
  };

  const getTimeframeLabel = (timeframe: string) => {
    return TIMEFRAMES.find((tf) => tf.value === timeframe)?.label || timeframe;
  };

  const formatProvenanceSummary = (
    provenance?: DirectionalThesis['marketDataProvenance'],
  ) => {
    if (!provenance) return null;
    const barLabel = provenance.barSeries?.daily && provenance.barSeries?.intraday5m
      ? 'daily + 5m'
      : provenance.barIntervalLabel;
    const spotLagMin = provenance.spotFreshnessLagMs
      ? Math.round(provenance.spotFreshnessLagMs / 60000)
      : null;
    const barLagMin = Math.round(provenance.freshnessLagMs / 60000);
    return `${barLabel} bars · live quote${spotLagMin !== null ? ` ${spotLagMin}m ago` : ''} · last bar ${barLagMin}m ago`;
  };

  const renderTimeframeCard = (timeframe: {
    value: '1_day' | '1_2_weeks' | '1_month' | '6_months_plus';
    label: string;
  }) => {
    const timeframeTheses = theses[timeframe.value] || [];
    const upThesis = timeframeTheses.find((t) => t.direction === 'up');
    const sidewaysThesis = timeframeTheses.find(
      (t) => t.direction === 'sideways',
    );
    const downThesis = timeframeTheses.find((t) => t.direction === 'down');
    const isLoading = loading[timeframe.value];

    // Find the highest confidence direction
    const allTheses = [upThesis, sidewaysThesis, downThesis].filter(Boolean);
    const highestConfidence = Math.max(
      ...allTheses.map((t) => t?.confidence || 0),
    );
    const highestConfidenceDirection = allTheses.find(
      (t) => t?.confidence === highestConfidence,
    )?.direction;

    return (
      <Card key={timeframe.value}>
        <CardHeader>
          <CardTitle>{timeframe.label}</CardTitle>
          {upThesis?.marketDataProvenance && (
            <p className="text-xs text-gray-500 mt-1">
              {formatProvenanceSummary(upThesis.marketDataProvenance)}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-3">
              {renderDirectionCard(
                upThesis,
                'up',
                timeframe.value,
                highestConfidenceDirection === 'up',
              )}
              {renderDirectionCard(
                sidewaysThesis,
                'sideways',
                timeframe.value,
                highestConfidenceDirection === 'sideways',
              )}
              {renderDirectionCard(
                downThesis,
                'down',
                timeframe.value,
                highestConfidenceDirection === 'down',
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderDirectionCard = (
    thesis: DirectionalThesis | undefined,
    direction: 'up' | 'sideways' | 'down',
    timeframe: string,
    isHighest: boolean = false,
  ) => {
    if (!thesis) {
      return (
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getDirectionIcon(direction)}
              <span className="font-medium text-gray-700 capitalize text-sm">
                {direction}
              </span>
            </div>
            <span className="text-xs text-gray-500">No thesis</span>
          </div>
        </div>
      );
    }

    const previousConfidence = thesis.previousContext?.confidence;
    const confidenceChange =
      previousConfidence !== undefined
        ? thesis.confidence - previousConfidence
        : null;

    const cardBgColor = getDirectionCardBgColor(
      direction,
      thesis.confidence,
      isHighest,
    );

    return (
      <div
        className={`rounded-lg p-3 cursor-pointer hover:shadow-md transition-all ${cardBgColor} ${
          isHighest ? 'ring-2 ring-offset-1' : ''
        }`}
        onClick={() => handleDirectionClick(thesis)}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {getDirectionIcon(direction)}
            <span
              className={`font-semibold capitalize text-sm ${
                isHighest ? 'text-gray-900' : 'text-gray-800'
              }`}
            >
              {direction}
              {isHighest && (
                <span className="ml-1 text-xs font-bold text-gray-600">
                  (Most Probable)
                </span>
              )}
            </span>
          </div>
          <div className="text-right">
            <div
              className={`text-2xl font-bold ${getConfidenceColor(thesis.confidence)} px-3 py-1 rounded-lg shadow-sm`}
            >
              {thesis.confidence}%
            </div>
            {confidenceChange !== null && (
              <div
                className={`text-xs mt-0.5 font-medium ${
                  confidenceChange > 0
                    ? 'text-green-600'
                    : confidenceChange < 0
                      ? 'text-red-600'
                      : 'text-gray-600'
                }`}
              >
                {confidenceChange > 0 ? '+' : ''}
                {confidenceChange.toFixed(1)}%
              </div>
            )}
          </div>
        </div>

        {/* Confidence bar */}
        <div className="w-full bg-gray-300 rounded-full h-2 mb-2">
          <div
            className={`h-2 rounded-full transition-all ${getConfidenceBarColor(thesis.confidence)} ${
              isHighest ? 'shadow-sm' : ''
            }`}
            style={{ width: `${thesis.confidence}%` }}
          />
        </div>

        <div className="text-xs text-gray-700 line-clamp-2 font-medium">
          {thesis.scenario}
        </div>
        <div className="mt-1.5 flex justify-end">
          <span className="text-[10px] text-gray-400 font-normal">
            {formatRelativeTime(thesis.generatedAt)}
          </span>
        </div>
      </div>
    );
  };

  const renderModal = () => {
    if (!isModalOpen || !selectedThesis) return null;

    const thesisHistory =
      history[selectedThesis.timeframe]?.[selectedThesis.direction] || [];

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={closeModal}
      >
        <div
          className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              {getDirectionIcon(selectedThesis.direction)}
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {getTimeframeLabel(selectedThesis.timeframe)} -{' '}
                  {selectedThesis.direction.toUpperCase()} Direction
                </h2>
                <p className="text-sm text-gray-600">
                  {selectedThesis.scenario}
                </p>
              </div>
            </div>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="space-y-6">
              {/* Confidence Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Confidence Score
                  </h3>
                  <div
                    className={`text-3xl font-bold ${getConfidenceColor(selectedThesis.confidence)} px-4 py-2 rounded`}
                  >
                    {selectedThesis.confidence}%
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${getConfidenceBarColor(selectedThesis.confidence)}`}
                    style={{ width: `${selectedThesis.confidence}%` }}
                  />
                </div>
                {selectedThesis.previousContext && (
                  <div className="mt-3 text-sm text-gray-600">
                    <span>
                      Previous: {selectedThesis.previousContext.confidence}%
                    </span>
                    {selectedThesis.confidence -
                      selectedThesis.previousContext.confidence !==
                      0 && (
                      <span
                        className={`ml-2 ${
                          selectedThesis.confidence -
                            selectedThesis.previousContext.confidence >
                          0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        (
                        {selectedThesis.confidence -
                          selectedThesis.previousContext.confidence >
                        0
                          ? '+'
                          : ''}
                        {(
                          selectedThesis.confidence -
                          selectedThesis.previousContext.confidence
                        ).toFixed(1)}
                        %)
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Previous Performance */}
              {selectedThesis.previousContext && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Previous Performance
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">
                        Previous Confidence:
                      </span>
                      <span className="ml-2 font-medium">
                        {selectedThesis.previousContext.confidence}%
                      </span>
                    </div>
                    {selectedThesis.previousContext.performance && (
                      <>
                        <div>
                          <span className="text-gray-600">
                            Direction Match:
                          </span>
                          <span
                            className={`ml-2 font-medium ${
                              selectedThesis.previousContext.performance
                                .directionMatch
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                          >
                            {selectedThesis.previousContext.performance
                              .directionMatch
                              ? '✓ Correct'
                              : '✗ Incorrect'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            Avg Price Deviation:
                          </span>
                          <span className="ml-2 font-medium">
                            {selectedThesis.previousContext.performance.averageDeviation.toFixed(
                              2,
                            )}
                            %
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Generated:</span>
                          <span className="ml-2 font-medium">
                            {formatDate(
                              selectedThesis.previousContext.generatedAt,
                            )}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Historical Confidence Trend */}
              {thesisHistory.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Historical Confidence Trend
                  </h3>
                  <div className="space-y-3">
                    {thesisHistory.slice(0, 10).map((historicalThesis) => (
                      <div
                        key={historicalThesis._id}
                        className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex flex-col">
                            <span className="text-xs text-gray-500">
                              {formatDate(historicalThesis.generatedAt)}
                            </span>
                            {historicalThesis.targetDate && (
                              <span className="text-[10px] text-gray-400 mt-0.5">
                                Target: {historicalThesis.targetDate}
                              </span>
                            )}
                          </div>
                          <span
                            className={`font-medium text-sm ${getConfidenceColor(historicalThesis.confidence)} px-2 py-1 rounded`}
                          >
                            {historicalThesis.confidence}%
                          </span>
                        </div>
                        {historicalThesis.targetPrice && (
                          <div className="grid grid-cols-3 gap-2 text-xs mt-2 pt-2 border-t border-gray-200">
                            {historicalThesis.targetPrice.nasdaq && (
                              <div>
                                <div className="text-gray-500 mb-0.5">
                                  NASDAQ
                                </div>
                                <div className="font-medium text-gray-700">
                                  $
                                  {historicalThesis.targetPrice.nasdaq.current.toFixed(
                                    2,
                                  )}
                                </div>
                              </div>
                            )}
                            {historicalThesis.targetPrice.dow && (
                              <div>
                                <div className="text-gray-500 mb-0.5">DOW</div>
                                <div className="font-medium text-gray-700">
                                  $
                                  {historicalThesis.targetPrice.dow.current.toFixed(
                                    2,
                                  )}
                                </div>
                              </div>
                            )}
                            {historicalThesis.targetPrice.nyse && (
                              <div>
                                <div className="text-gray-500 mb-0.5">NYSE</div>
                                <div className="font-medium text-gray-700">
                                  $
                                  {historicalThesis.targetPrice.nyse.current.toFixed(
                                    2,
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Target Prices */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Target Prices
                </h3>
                {selectedThesis.targetPrice ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    {selectedThesis.targetPrice.nasdaq && (
                      <div>
                        <div className="text-gray-600 mb-1">NASDAQ</div>
                        <div className="font-medium">
                          $
                          {selectedThesis.targetPrice.nasdaq.current.toFixed(2)}{' '}
                          → $
                          {selectedThesis.targetPrice.nasdaq.target.toFixed(2)}
                        </div>
                      </div>
                    )}
                    {selectedThesis.targetPrice.dow && (
                      <div>
                        <div className="text-gray-600 mb-1">DOW</div>
                        <div className="font-medium">
                          ${selectedThesis.targetPrice.dow.current.toFixed(2)} →
                          ${selectedThesis.targetPrice.dow.target.toFixed(2)}
                        </div>
                      </div>
                    )}
                    {selectedThesis.targetPrice.nyse && (
                      <div>
                        <div className="text-gray-600 mb-1">NYSE</div>
                        <div className="font-medium">
                          ${selectedThesis.targetPrice.nyse.current.toFixed(2)}{' '}
                          → ${selectedThesis.targetPrice.nyse.target.toFixed(2)}
                        </div>
                      </div>
                    )}
                    {!selectedThesis.targetPrice.nasdaq &&
                      !selectedThesis.targetPrice.dow &&
                      !selectedThesis.targetPrice.nyse && (
                        <div className="text-gray-500 italic md:col-span-3">
                          Target prices not available for this thesis
                        </div>
                      )}
                    <div className="md:col-span-3 mt-2">
                      <span className="text-gray-600">Target Date: </span>
                      <span className="font-medium">
                        {selectedThesis.targetDate}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-gray-500 italic mb-2">
                      Target prices not available for this thesis
                    </div>
                    <div className="mt-2">
                      <span className="text-gray-600">Target Date: </span>
                      <span className="font-medium">
                        {selectedThesis.targetDate}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Key Factors */}
              {selectedThesis.keyFactors.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Key Factors
                  </h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                    {selectedThesis.keyFactors.map((factor, idx) => (
                      <li key={idx}>{factor}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sector Rankings */}
              {selectedThesis.sectorRankings.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Top Sectors
                  </h3>
                  <div className="space-y-2">
                    {selectedThesis.sectorRankings
                      .slice(0, 5)
                      .map((sector, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="text-gray-600">{sector.sector}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${sector.correlation}%` }}
                              />
                            </div>
                            <span className="font-medium w-12 text-right">
                              {sector.correlation}%
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Confirmation & Failing Signals */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedThesis.confirmationSignals.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-green-900 mb-3">
                      Confirmation Signals
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                      {selectedThesis.confirmationSignals.map((signal, idx) => (
                        <li key={idx}>{signal}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedThesis.failingSignals.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-red-900 mb-3">
                      Failing Signals
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                      {selectedThesis.failingSignals.map((signal, idx) => (
                        <li key={idx}>{signal}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Suggested Strategies */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Suggested Strategies by Risk Tier
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {Object.entries(selectedThesis.suggestedStrategy).map(
                    ([tier, strategy]) => (
                      <div
                        key={tier}
                        className="border border-gray-200 rounded p-2"
                      >
                        <div className="font-medium text-gray-900 mb-1">
                          Tier {tier.replace('tier', '')}
                        </div>
                        <div className="text-gray-600 text-xs">{strategy}</div>
                      </div>
                    ),
                  )}
                </div>
              </div>

              {/* Market Context */}
              <div className="bg-gray-50 rounded-lg p-4 text-sm">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Market Context
                </h3>
                <div className="space-y-1 text-gray-600">
                  <div>
                    <span className="font-medium">Overall Sentiment: </span>
                    {selectedThesis.marketContext.overallSentiment}
                  </div>
                  <div>
                    <span className="font-medium">Data Reference: </span>
                    {formatDate(selectedThesis.marketContext.dataDateReference)}
                  </div>
                  {selectedThesis.marketDataProvenance && (
                    <div>
                      <span className="font-medium">Market Data: </span>
                      {formatProvenanceSummary(
                        selectedThesis.marketDataProvenance,
                      )}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Generated: </span>
                    {formatDate(selectedThesis.generatedAt)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Market Thesis</h1>
          <p className="mt-1 text-sm text-gray-500">
            Directional market predictions with confidence scores and historical
            context
          </p>
        </div>
        <button
          onClick={loadAllTheses}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {TIMEFRAMES.map((timeframe) => renderTimeframeCard(timeframe))}
      </div>

      {renderModal()}
    </div>
  );
};

export default MarketThesis;
